import { Box, Fade, Grid, Radio, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Email, EmailOutlined, FileCopyOutlined, Send, Sms, SmsOutlined } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { GlobalStore, Layout, LoginLogoImg, useBodyBackgroundColor } from "@roc/feature-app-core";
import { iphoneImage } from "@roc/feature-app-core/assets";
import { Button, NumberFormat, TextField } from "@roc/ui";
import { TextField as MuiTextField } from "@material-ui/core";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { renderToString } from 'react-dom/server';

import swal from "sweetalert";
import { useStore } from "../../hooks/useStore";
import { debounceRequest, isNil, validateEmail } from "@roc/feature-utils";
import { RequestCreditAuthStore } from "./stores/requestCreditAuthStore";

const purple = "#652173";
const purpleLight = "#fdf3ff";

const useSmsStyles = makeStyles({
  smsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  phoneScreen: {
    width: "328px",
    height: "670px",
    backgroundImage: `url(${iphoneImage})`,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    position: "relative",
    padding: "24px 24px 0 30px",
  },
  messageBubble: {
    backgroundColor: "#4CAF50",
    color: "white",
    padding: "12px",
    borderRadius: "12px",
    maxWidth: "80%",
    textAlign: "left",
    marginBottom: "16px",
    marginTop: "40px",
  },
});

const SMSPreview = ({ borrowerName, link, runCredit, tpoName }) => {
  const classes = useSmsStyles();

  return (
    <div className={classes.smsContainer}>
      <div className={classes.phoneScreen}>
        <div className={classes.messageBubble}>
          <p>Hello {borrowerName} 👋,</p>
          <p>{`Please complete the${runCredit ? ' credit authorization' : ''} form using the link below.`}</p>
          <p style={{
            wordBreak: 'break-all'
          }}>
            <a target="_blank" rel="noopener noreferrer" href={link} style={{ color: "#fff", textDecoration: "underline" }}>
              {link}
            </a>
          </p>
          <p>- {tpoName}</p>
        </div>
      </div>
    </div>
  );
};

const useEmailStyles = makeStyles({
  emailContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflowWrap: 'anywhere',
  },
  emailCard: {
    width: "100%",
    backgroundColor: "#444",
    color: "#ddd",
    borderRadius: "32px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
  emailBody: {
    padding: "24px",
  },
  emailContent: {
    backgroundColor: "#fff",
    color: "#111",
    padding: "16px",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16
  },
  linkStyle: {
    color: "#007BFF",
    textDecoration: "none",
  },
});

export const getRequestCreditAuthSubject = (runCredit: boolean) => {
  return `Action Required: Complete Your ${runCredit ? 'Credit Authorization' : 'Information'}`;
}

export const getRequestCreditAuthEmailBodyContent = (borrowerName: string, link: string, runCredit: boolean, tpoName: string) => {
  return <>
    <p>Hello {borrowerName},</p>
    <br></br>
    <p>{`Please complete the${runCredit ? ' credit authorization' : ''} form using the link below.`}</p>
    <p>{link ? <a target="_blank" rel="noopener noreferrer" href={link} style={{
      color: "#007BFF",
      textDecoration: "none",
    }}>{runCredit ? 'Credit Authorization Form' : 'Personal Information'}</a>
      : (<strong>{`<your${runCredit ? ' credit authorization' : ''} link here>`}</strong>)}</p>
    <br></br>
    <p>Let us know if you need any help!</p>
    <br></br>
    <p>Thank you,</p>
    <p>{tpoName}</p>
  </>
}

const EmailPreview = ({ subject, toEmail, fromEmail, bodyContent }) => {
  const classes = useEmailStyles();

  return (
    <div className={classes.emailContainer}>
      <div className={classes.emailCard}>
        <div className={classes.emailBody}>
          <p><strong>From:</strong> {fromEmail}</p>
          <p><strong>To:</strong> {toEmail}</p>
          <p><strong>Subject:</strong> {subject}</p>
          <br></br>
          <div className={classes.emailContent}>
            <Box><LoginLogoImg imgStyle={{
              maxWidth: 100
            }} /></Box>
            {bodyContent}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) =>
  createStyles({
    option: {
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(1, 2),
      border: "1px solid #ccc",
      borderRadius: 16,
      cursor: "pointer",
      color: "#111",
      backgroundColor: "#fff",
      '&:hover': {
        backgroundColor: purpleLight,
      }
    },
    option_selected: {
      color: "#FFF",
      backgroundColor: purple,
      '&:hover': {
        backgroundColor: purple,
      }
    },
    swal: {
      borderRadius: 32,
      '& .swal-button--redirect': {
        backgroundColor: theme.palette.primary.main,
        boxShadow: 'none !important',
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        }
      },
      '& .swal-title': {
        color: '#111',
      },
      '& .swal-text': {
        color: '#111',
        textAlign: 'center',
      },
      '& .swal-icon--success__line--long': {
        backgroundColor: '#00b600',
      },
      '& .swal-icon--success__line--tip': {
        backgroundColor: '#00b600',
      },
      '& .swal-icon--success__ring': {
        borderColor: '#64e164',
      },
    }
  })
);

const useBorrowerAutocompleteFieldStyles = makeStyles(theme => ({
  autocomplete: {
    '& .MuiAutocomplete-endAdornment': {
      display: 'none',
    },
  },
}));

export const BorrowerAutocompleteField = observer(
  ({ selectedBorrower, setSelectedBorrower, store, globalStore }: { globalStore: GlobalStore; store: RequestCreditAuthStore; selectedBorrower: BorrowerOption; setSelectedBorrower: (value: BorrowerOption) => void }) => {

    const classes = useBorrowerAutocompleteFieldStyles();
    const options = store.borrowerOptions;

    const fetchBorrowerOptions = debounceRequest((cancelToken, name) => {
      if (globalStore?.userFeatures?.displayLeads) {
        store.findBorrowersAndLeadsByName(name, cancelToken)
      } else {
        store.findBorrowersByName(name, cancelToken)
      }
    }
    )

    return (
      <Box className={classes.autocomplete}>
        <Autocomplete
          id={`autocomplete-borrower`}
          inputValue={selectedBorrower?.name}
          onInputChange={(e, value) => {
            if (e?.nativeEvent?.type === 'input') {
              setSelectedBorrower({
                name: value
              });
              fetchBorrowerOptions(value);
            }
          }}
          options={options}
          renderInput={params => (
            <MuiTextField
              variant={'outlined'}
              label={'Borrower'}
              placeholder="Search by name"
              {...params}
            />
          )}
          getOptionLabel={(option: any) => option?.label ?? ''}
          getOptionSelected={(option: any, value) =>
            option.label === value.label
          }
          onChange={async (event, opt) => {
            const data = await store.fetchRecordData(opt.value);
            setSelectedBorrower({
              name: data.name,
              phone: ((data.phone ?? '') as string).replace(/\D/g, ''),
              email: data.email,
              borrowerId: data?.borrowerId,
              leadId: data?.leadId,
              firstName: data?.firstName,
              lastName: data?.lastName,
              leadSource: data?.leadSource
            });
          }}
          fullWidth
          PopperComponent={options.length ? undefined : () => null}
        />
      </Box>
    );
  }
);

interface BorrowerOption {
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
  email?: string;
  borrowerId?: number;
  leadId?: number;
  leadSource?: string;
}

export const RequestCreditAuthorization = observer(() => {
  const classes = useStyles();
  const { requestCreditAuthStore, globalStore, userStore } = useStore();
  const isInternalLender = globalStore.lenderInfo.isInternal;
  const [channel, setChannel] = useState<undefined | 'EMAIL' | 'SMS'>('EMAIL');
  const [borrowerMode, setBorrowerMode] = useState<undefined | 'EXISTING' | 'NEW'>(isInternalLender ? 'EXISTING' : undefined);
  const [selectedBorrower, setSelectedBorrower] = useState<BorrowerOption>({});
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [cellPhone, setCellPhone] = useState<string>('');
  const [borrowerId, setBorrowerId] = useState<number>(null);
  const [runCredit, setRunCredit] = useState<boolean>(null);
  const [leadId, setLeadId] = useState<number>(null);
  const [leadSource, setLeadSource] = useState<string>(null);


  useBodyBackgroundColor("#fff");

  useEffect(() => {
    if (borrowerMode) {
      if (borrowerMode == 'NEW') {
        setSelectedBorrower(undefined);
        setEmailAddress('');
        setCellPhone('');
        setFirstName('');
        setLastName('');
        setFullName('');
        setBorrowerId(null);
        setLeadId(null);
        setLeadSource('')
      }
      else {
        setEmailAddress(selectedBorrower?.email ?? '');
        setCellPhone(selectedBorrower?.phone ?? '');
        setFirstName(selectedBorrower?.firstName ?? '');
        setLastName(selectedBorrower?.lastName ?? '');
        setFullName(selectedBorrower?.name ?? '');
        setBorrowerId(selectedBorrower?.borrowerId ?? null);
        setLeadId(selectedBorrower?.leadId ?? null);
        setLeadSource(selectedBorrower?.leadSource ?? null)
      }
    }
  }, [borrowerMode, selectedBorrower]);

  useEffect(() => {
    if (borrowerMode) {
      if (borrowerMode == 'NEW') {
        let fullName = firstName;
        fullName += lastName != '' ? ' ' + lastName : '';
        setFullName(fullName);
      }
    }
  }, [firstName, lastName]);

  const getMessageConfig = async (runCredit: boolean) => {
    await requestCreditAuthStore.getMessageConfig(runCredit);
    setRunCredit(runCredit);
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(requestCreditAuthStore.messageConfig?.link);
    requestCreditAuthStore?.globalStore?.notificationStore.showSuccessNotification({
      message: 'Copied to clipboard',
    });
  };

  const subject = getRequestCreditAuthSubject(runCredit);
  const bodyContentToDisplay = getRequestCreditAuthEmailBodyContent(fullName, null, runCredit, requestCreditAuthStore.messageConfig?.tpoName);

  const handleSendEmail = async (callback?: () => void) => {
    const currentBorrowerId = await requestCreditAuthStore.handleSaveBorrower(runCredit, borrowerId, firstName, lastName, emailAddress, cellPhone, leadId, leadSource);
    if (currentBorrowerId == null) return;
    const bodyContentToSend = getRequestCreditAuthEmailBodyContent(fullName, requestCreditAuthStore.messageConfig?.link, runCredit, requestCreditAuthStore.messageConfig?.tpoName);
    const emailBodyContent = renderToString(bodyContentToSend);
    requestCreditAuthStore.handleSendEmail(runCredit, currentBorrowerId, emailAddress, subject, emailBodyContent, callback);
  }

  const emailAddressError = !emailAddress || validateEmail(emailAddress) ? "" : "Please enter a valid email.";

  const hasFirstName = firstName && firstName.trim().length > 0;
  const hasLastName = lastName && lastName.trim().length > 0;
  const hasFullName = fullName && fullName.trim().length > 0;
  const hasBorrowerName = (borrowerMode == 'NEW' ? (hasFirstName && hasLastName) : hasFullName);
  const sendButtonDisabled = ((hasBorrowerName && (emailAddress || cellPhone)) ? false : true) || (emailAddress && !!emailAddressError);

  return (
    <Layout maxWidth={'lg'}>
      <Box my={4} textAlign={'left'}>
        <Typography variant={'h3'}>
          Request Authorization
        </Typography>
      </Box>

      <Box mb={4} fontSize={24}>
        What type of request would you like to do? Choose an option below:
      </Box>

      <Box>
        <Grid container>
          <Grid item xs={12} lg={5}>
            <Box mb={4} fontSize={18} display={"flex"} gridGap={16} maxWidth={500}>
              <Box
                onClick={() => getMessageConfig(false)}
                className={clsx(classes.option, runCredit === false ? classes.option_selected : '')}>
                <Radio
                  checked={runCredit === false}
                  onChange={() => { }}
                  value={channel}
                  name="bg-radio"
                  style={{
                    color: runCredit === false ? purpleLight : '#777'
                  }}
                />
                <Box component={"span"} ml={1}>Background</Box>
              </Box>
              <Box
                onClick={() => getMessageConfig(true)}
                className={clsx(classes.option, runCredit === true ? classes.option_selected : '')}>
                <Radio
                  checked={runCredit === true}
                  onChange={() => { }}
                  value={channel}
                  name="bg-credit-radio"
                  style={{
                    color: runCredit === true ? purpleLight : '#777'
                  }}
                />
                <Box component={"span"} ml={1}>Background and Credit</Box>
              </Box>
            </Box>
          </Grid>
          {!isInternalLender && !globalStore?.userFeatures?.usingPrivateLenderPortal && isNil(borrowerMode) &&
            <Grid item xs={12}>
              <Fade in={runCredit != null}>
                <div>
                  {
                    runCredit != null &&
                    <>
                      <Box mb={4} p={2} borderRadius={16} textAlign={'left'} display={'flex'} alignItems={'center'} bgcolor={purpleLight}>
                        <Box flexGrow={1} fontSize={20}>
                          {requestCreditAuthStore.messageConfig?.link}
                        </Box>
                        <Box>
                          <Button testId="copyLink" variant="outlined" size="large" startIcon={<FileCopyOutlined />} onClick={handleCopyLink}>
                            Copy
                          </Button>
                        </Box>
                      </Box>
                    </>
                  }
                </div>
              </Fade>
            </Grid>
          }
        </Grid>
      </Box>

      <Fade in={runCredit != null}>
        <div>
          {
            runCredit != null &&
            <>
              {/* <Box mb={4} fontSize={24}>
                How would you like to send the authorization link to the borrower? Choose an option below:
              </Box> */}

              <Box mb={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={5}>
                    {/* <Box mb={4} fontSize={18} display={"flex"} gridGap={16} maxWidth={500}>
                      <Box
                        onClick={() => setChannel('EMAIL')}
                        className={clsx(classes.option, channel == "EMAIL" ? classes.option_selected : '')}>
                        <Radio
                          checked={channel == "EMAIL"}
                          onChange={() => { }}
                          value={channel}
                          name="email-radio"
                          style={{
                            color: channel == "EMAIL" ? purpleLight : '#777'
                          }}
                        />
                        {channel == "EMAIL" ? <Email /> : <EmailOutlined />}
                        <Box component={"span"} ml={1}>Send via Email</Box>
                      </Box>
                      <Box
                        onClick={() => setChannel('SMS')}
                        className={clsx(classes.option, channel == "SMS" ? classes.option_selected : '')}>
                        <Radio
                          checked={channel == "SMS"}
                          onChange={() => { }}
                          value={channel}
                          name="email-sms"
                          style={{
                            color: channel == "SMS" ? purpleLight : '#777'
                          }}
                        />
                        {channel == "SMS" ? <Sms /> : <SmsOutlined />}
                        <Box component={"span"} ml={1}>Send via SMS</Box>
                      </Box>
                    </Box> */}
                    <Fade in={runCredit != null && channel != undefined}>
                      <div>
                        {
                          runCredit != null && channel &&
                          <>
                            {!isInternalLender ?
                              <>
                                <Box mb={4} fontSize={24}>
                                  Would you like to send it to an existing borrower or a new one?
                                </Box>
                                <Box mb={4} fontSize={18} display={"flex"} gridGap={16} maxWidth={500}>
                                  <Box
                                    onClick={() => setBorrowerMode('EXISTING')}
                                    className={clsx(classes.option, borrowerMode == "EXISTING" ? classes.option_selected : '')}>
                                    <Radio
                                      checked={borrowerMode == "EXISTING"}
                                      onChange={() => { }}
                                      value={borrowerMode}
                                      name="existing-borrower-radio"
                                      style={{
                                        color: borrowerMode == "EXISTING" ? purpleLight : '#777'
                                      }}
                                    />
                                    <Box component={"span"} ml={1}>Existing Borrower</Box>
                                  </Box>
                                  <Box
                                    onClick={() => setBorrowerMode('NEW')}
                                    className={clsx(classes.option, borrowerMode == "NEW" ? classes.option_selected : '')}>
                                    <Radio
                                      checked={borrowerMode == "NEW"}
                                      onChange={() => { }}
                                      value={borrowerMode}
                                      name="existing-borrower-radio"
                                      style={{
                                        color: borrowerMode == "NEW" ? purpleLight : '#777'
                                      }}
                                    />
                                    <Box component={"span"} ml={1}>New Borrower</Box>
                                  </Box>
                                </Box>
                              </>
                              :
                              <Box mb={4} fontSize={24}>
                                Please choose an existing borrower
                              </Box>
                            }
                            <Fade in={runCredit != null && channel != undefined}>
                              <div>
                                {
                                  borrowerMode &&
                                  <>
                                    {
                                      borrowerMode == 'EXISTING' ?
                                        <>
                                          <Box mb={4} fontSize={32}>
                                            <BorrowerAutocompleteField
                                              selectedBorrower={selectedBorrower}
                                              setSelectedBorrower={setSelectedBorrower}
                                              store={requestCreditAuthStore}
                                              globalStore={globalStore}
                                            />
                                          </Box>
                                        </> :
                                        <>
                                          <Box mb={4} fontSize={32}>
                                            <TextField
                                              testId="firstName"
                                              required
                                              value={firstName}
                                              variant="outlined"
                                              label="First Name"
                                              onChange={e => {
                                                setFirstName(e.target.value);
                                              }}
                                              fullWidth
                                            />
                                          </Box>
                                          <Box mb={4} fontSize={32}>
                                            <TextField
                                              testId="lastName"
                                              required
                                              value={lastName}
                                              variant="outlined"
                                              label="Last Name"
                                              onChange={e => {
                                                setLastName(e.target.value);
                                              }}
                                              fullWidth
                                            />
                                          </Box>
                                        </>
                                    }
                                    {
                                      channel == "EMAIL" &&
                                      <Box fontSize={32}>
                                        <TextField
                                          testId="emailAddress"
                                          required
                                          value={emailAddress}
                                          variant="outlined"
                                          label="Email Address"
                                          onChange={e => {
                                            setEmailAddress(e.target.value);
                                          }}
                                          fullWidth
                                          disabled={borrowerMode === 'EXISTING' && !!selectedBorrower?.email}
                                          helperText={emailAddressError}
                                          error={!!emailAddressError}
                                        />
                                      </Box>
                                    }
                                    {
                                      channel == "SMS" &&
                                      <Box fontSize={32}>
                                        <NumberFormat
                                          testId="cellPhone"
                                          customInput={TextField}
                                          format="(###) ###-####"
                                          mask="_"
                                          variant="outlined"
                                          label="Cell Phone"
                                          fullWidth
                                          required={true}
                                          value={cellPhone}
                                          onChange={e => {
                                            setCellPhone(e.target.value);
                                          }}
                                        />
                                      </Box>
                                    }
                                    <Box my={3} fontSize={32}>
                                      <Button testId="send" variant='contained' color='primary' endIcon={<Send />} size='large'
                                        disabled={sendButtonDisabled}
                                        onClick={() => {
                                          handleSendEmail(() => {
                                            swal({
                                              title: "Success!",
                                              text: `The link has been sent successfully. Please ask the borrower to check their message and complete the process.`,
                                              icon: "success",
                                              buttons: {
                                                redirect: {
                                                  text: "Ok",
                                                },
                                              },
                                              className: classes.swal,
                                              closeOnEsc: false,
                                              closeOnClickOutside: false,
                                            }).then(async (v) => {
                                              setFullName('');
                                              setEmailAddress('');
                                              setCellPhone('');
                                              setBorrowerMode(isInternalLender ? 'EXISTING' : undefined);
                                              setSelectedBorrower(undefined);
                                              setRunCredit(null);
                                              requestCreditAuthStore.reset();
                                            });
                                          })
                                        }}
                                        style={{
                                          padding: '16px 24px'
                                        }}>
                                        {channel == "EMAIL" ? "Send Via Email" : "Send"}
                                      </Button>
                                    </Box>
                                  </>
                                }
                              </div>
                            </Fade>
                          </>
                        }
                      </div>
                    </Fade>

                  </Grid>
                  {
                    runCredit != null && channel && borrowerMode &&

                    <Grid item xs={12} lg={7}>
                      <Box px={5} width={"100%"}>
                        <Box display={'flex'} justifyContent={'center'} fontSize={24} my={3} fontWeight={"bold"}>
                          {channel == 'SMS' ? 'Preview' : 'Preview'}
                        </Box>
                        {
                          channel == 'SMS' && <SMSPreview borrowerName={fullName} link={requestCreditAuthStore.messageConfig?.link} runCredit={runCredit} tpoName={requestCreditAuthStore.messageConfig?.tpoName} />
                        }
                        {
                          channel == 'EMAIL' && <EmailPreview subject={subject} toEmail={emailAddress} fromEmail={requestCreditAuthStore.messageConfig?.fromEmail} bodyContent={bodyContentToDisplay} />
                        }
                      </Box>
                    </Grid>
                  }
                </Grid>
              </Box>
            </>
          }
        </div>
      </Fade>

    </Layout>
  )

});